import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingBag, ArrowRight, Shield, Clock, Lock } from "lucide-react";
import { Link } from "wouter";
import { useCart } from "@/contexts/CartContext";

const VAT_RATE = 0.20;

export function CartDrawer() {
  const { items, drawerOpen, closeDrawer, clearCart, removeItem } = useCart();
  const subtotal = items.reduce((acc, curr) => acc + curr.price, 0);
  const tax = +(subtotal * VAT_RATE).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);

  return (
    <AnimatePresence mode="wait">
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeDrawer}
            className="fixed inset-0 bg-[#0F1B2D]/40 backdrop-blur-sm z-[60]"
          />

          {/* Drawer */}
          <motion.div
            key="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-[70] flex flex-col shadow-[0_0_50px_rgba(15,27,45,0.15)] border-l border-slate-100"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-8 py-6 border-b border-slate-50 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F0A500]/10 flex items-center justify-center text-[#F0A500]">
                  <ShoppingBag size={20} />
                </div>
                <div>
                  <span className="block font-bold text-[#0D1520] text-lg tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Your Order</span>
                  <span className="block text-[10px] font-black text-slate-400 uppercase tracking-[2px]">Elite Purchase Scene</span>
                </div>
              </div>
              <button
                onClick={closeDrawer}
                className="w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-[#0D1520] transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-8 py-8 custom-scrollbar">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-20">
                  <div className="w-24 h-24 rounded-3xl bg-slate-50 flex items-center justify-center mb-8">
                     <ShoppingBag className="w-10 h-10 text-slate-200" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0D1520] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Your cart is empty</h3>
                  <p className="text-slate-500 text-sm max-w-[200px] leading-relaxed">Select an Elite plan to unlock premium IB resources and expert examiner support.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {items.map((item, idx) => (
                    <div key={`${item.id}-${idx}`} className="bg-[#F8FAFC] rounded-[28px] border border-slate-200 p-6 relative overflow-hidden group shadow-sm transition-all duration-300 hover:shadow-md">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-[#F0A500]/5 blur-3xl rounded-full -mr-16 -mt-16" />
                      
                      <div className="flex items-start justify-between mb-6 relative z-10">
                        <div className="flex-1 pr-4">
                          <div className="inline-flex px-2 py-0.5 rounded-md bg-[#2E8B6B]/10 text-[#2E8B6B] text-[9px] font-black uppercase tracking-[1px] mb-2">Selected Plan</div>
                          <h3 className="font-bold text-[#0D1520] text-lg mb-1 leading-tight">{item.name}</h3>
                          <p className="text-slate-500 text-xs font-medium line-clamp-1">{item.tagline}</p>
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-[10px] font-bold text-red-500/60 hover:text-red-500 uppercase tracking-widest transition-all p-1"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="flex items-center justify-between items-end relative z-10">
                        <div className="flex items-end gap-1">
                          <span className="text-2xl font-black text-[#0D1520]">£{item.price.toLocaleString()}</span>
                          <span className="text-[10px] font-bold text-slate-400 mb-1">{item.unit}</span>
                        </div>
                        
                        {item.hours && (
                          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 bg-white px-2.5 py-1 rounded-lg border border-slate-100 shadow-sm font-bold">
                            <Clock className="w-3 h-3 text-[#F0A500]" />
                            {item.hours}h Credits
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Price Breakdown */}
                  <div className="bg-[#F8FAFC]/50 rounded-3xl p-6 border border-slate-100 space-y-4 shadow-inner">
                    <div className="flex justify-between text-sm font-medium text-slate-500">
                      <span>Subtotal</span>
                      <span className="text-[#0D1520]">£{subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium text-slate-500">
                      <span>Surcharge (VAT 20%)</span>
                      <span className="text-[#0D1520]">£{tax.toFixed(2)}</span>
                    </div>
                    <div className="pt-4 border-t border-slate-200 flex justify-between items-baseline">
                      <span className="text-sm font-black text-slate-400 uppercase tracking-[2px]">Total Monthly</span>
                      <span className="text-2xl font-black text-[#F0A500]">£{total.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Trust Indicators */}
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { icon: Shield, label: "SSL SECURED" },
                      { icon: Lock, label: "ENCRYPTED" }
                    ].map((badge, i) => (
                      <div key={i} className="flex items-center gap-2 justify-center py-3 rounded-2xl bg-slate-50 border border-slate-100 text-[9px] font-black text-slate-500 tracking-[1px] uppercase">
                        <badge.icon size={12} className="text-[#2E8B6B]" />
                        {badge.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer CTA */}
            {items.length > 0 && (
              <div className="px-8 pb-10 pt-6 border-t border-slate-100 bg-white">
                <Link href="/checkout" onClick={closeDrawer}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-[#F0A500] hover:bg-[#D99500] text-white font-black uppercase tracking-[3px] py-5 rounded-[24px] flex items-center justify-center gap-3 transition-all duration-300 shadow-2xl shadow-[#F0A500]/20 cursor-pointer text-sm"
                  >
                    Proceed to Checkout
                    <ArrowRight className="w-4 h-4" />
                  </motion.div>
                </Link>
                <button
                  onClick={closeDrawer}
                  className="w-full text-center text-[10px] font-black text-slate-400 hover:text-[#0D1520] uppercase tracking-[2px] mt-6 transition-all"
                >
                  Continue Browsing
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
